import {MinRecommendation, ProblemDestination, TagScore} from '../state/global.types';
import {APIService} from '@/services/api.service';
import {FilterTag, TripOption, MatchedTag} from '@/views/amex/app-types';
import {AmexProblem, buildProblem} from './build-problem';
import {
  CandidatePoiItem,
  Price,
  UserActivityCategoryEnum,
  CandidateTransportationItem,
  Location,
} from '@mobi-components/global-solution';
import {TripIntent} from '@mobi-components/bluesky-utils';
import {mapAttributes} from './download-recommendations';
import {Dictionary} from 'vue-router/types/router';
import {fixDate} from '../state/fix-solution-dates';
import {TravelersIndormation} from './submit-amex-problem';

export interface Trip {
  context: CandidatePoiItem;
  theme: string;
  scheduled_start_time: number;
  scheduled_end_time: number;
  price: Price[];
  pois: string[];
  activities: MinRecommendation[];
  explanations: string[];
  accommodations: CandidatePoiItem[];
  flights: CandidateTransportationItem[];
  trip_id: string;
  flight_optional?: boolean;
}

export interface TripsWithExplanations {
  trips: Trip[];
  explanations: string[];
  recommendations?: TripOptionsRecommendation[];
}

export interface RecommendationsContext {
  id: string;
  location: Location;
  contextName: string;
  context: CandidatePoiItem;
}
export interface TripWithPois {
  trip: TripOption;
  pois: string[];
}

export type TripOptionsRecommendationCategory =
  | UserActivityCategoryEnum.FLIGHT
  | UserActivityCategoryEnum.ACCOMMODATION
  | UserActivityCategoryEnum.DINING
  | UserActivityCategoryEnum.EXPERIENCE;

export interface TripOptionsRecommendation {
  primary_category: TripOptionsRecommendationCategory;
  theme: string;
  pois: TripOptionsRecommendationPOI[];
  isLoading?: boolean;
}

export interface TripOptionsRecommendationPOI {
  id: string;
  name: string;
  description: string;
  images: {medium: string; small: string};
  prices: Price[];
  rating: number;
  tags: string[];
  types: string[];
  url: string;
  location: Location;
  transportation?: POITransportation;
}

interface POITransportation {
  segments: POITransportationSegment[];
}

interface POITransportationSegment {
  arrival_timestamp: number;
  cabin_class: string;
  carrier_code: string;
  carrier_name: string;
  departure_timestamp: number;
  destination: string;
  destination_timezone: string;
  number: string;
  origin: string;
  origin_timezone: string;
  type: string;
}

export function mapCandidatePoiItemToTripOption(
  x: CandidatePoiItem,
  total = 0,
  match = 0,
  matchedTags: MatchedTag[] = [],
): TripOption {
  return {
    ...x,
    location: x.location,
    id: x.id,
    total,
    match,
    matchedFilters: x.tags || [],
    matchedTags,
    name: x.name.replace(/_/g, ', '),
    contextName: x.name,
    images: [x.images.medium],
    artifitial: false,
    trip: true,
    intent: TripIntent.MUST_SEE,
    context: UserActivityCategoryEnum.CONTEXT,
    scheduled_start_time: fixDate(x.start_timestamp, x.location?.time_zone),
    scheduled_end_time: fixDate(x.end_timestamp, x.location?.time_zone),
    curated: false,
    prices: x.prices || [],
    recommendations: [],
    theme: null,
  };
}

export function mapPredifinedTripsToTripWithPoi(x: Trip, attributes: Dictionary<Dictionary<TagScore>>): TripWithPois {
  const matchedTags = mapAttributes(attributes, x.context);

  const match = 100;
  const total = -1;
  return {
    trip: {
      ...x,
      ...mapCandidatePoiItemToTripOption(
        {...x.context, start_timestamp: x.scheduled_start_time, end_timestamp: x.scheduled_end_time},
        total,
        match,
        matchedTags,
      ),
      theme: x.theme,
      prices: x.price || [],
    },
    pois: x.pois,
  };
}

export async function downloadDestinationTrips(
  id: string,
  userId: string,
  origin: ProblemDestination | null,
  destination: ProblemDestination | null,
  tags: FilterTag[],
  budget: Price[],
  travelersInformation: TravelersIndormation,
  additionalContexts: CandidatePoiItem[] = [],
  includeFlights = true,
): Promise<TripsWithExplanations> {
  if (!origin || !destination) {
    return {trips: [], explanations: []};
  }
  const additionalContextsPOIs = additionalContexts
    .sort((a, b) => a.end_timestamp - b.end_timestamp)
    .map((x) => ({
      candidate_information: {
        candidates: [x],
        id: x.id,
        name: x.name,
      },
    }));

  const problem = buildProblem(id, userId, origin, destination, tags, budget, travelersInformation, {
    [UserActivityCategoryEnum.ACCOMMODATION]: [],
    [UserActivityCategoryEnum.DINING]: [],
    [UserActivityCategoryEnum.EXPERIENCE]: [],
  });
  if (additionalContextsPOIs.length) {
    problem.activities = problem.activities
      .filter((x) => x.categories.includes(UserActivityCategoryEnum.CONTEXT))
      // @ts-ignore
      .concat(additionalContextsPOIs);
  }

  return downloadDestinationTripsByProblem(problem, includeFlights);
}

export async function downloadDestinationTripsByProblem(
  problem: AmexProblem,
  includeFlights = true,
): Promise<TripsWithExplanations> {
  try {
    const {results, explanations, recommendations} = await APIService.post<{
      results: Trip[];
      attributes: Dictionary<Dictionary<TagScore>>;
      explanations: string[];
      recommendations: TripOptionsRecommendation[];
    }>(`problem/primary-context-trips`, problem, {
      params: {
        include_flights: includeFlights,
      },
    });

    const trips = results.map((x) => ({
      ...x,
      // @ts-ignore
      scheduled_end_time: fixDate(x.scheduled_end_time, problem.destination.time_zone),
      // @ts-ignore
      scheduled_start_time: fixDate(x.scheduled_start_time, problem.origin.time_zone),
      prices: x.price,
    }));
    return {
      trips,
      explanations,
      recommendations,
    };
    // .map((x) => mapPredifinedTripsToTripWithPoi(x, attributes))
    // .map((x) => ({
    //   trip: {...x.trip, id: v4()},
    //   pois: x.pois,
    // }));
  } catch (e) {
    return {
      trips: [],
      explanations: [],
      recommendations: [],
    };
  }
}
